// V2-only Address Change section for Business, rendered inside the "What
// changed?" drawer.
//
// Mirrors AutomobilePolicy's V2 AddressChangeInformationV2 look-and-feel (see
// AutomobilePolicy/AutomobilePolicyChangeRequest/v2/components/AddressChangeInformationV2.tsx),
// adapted to Business — Business has no vehicles/drivers to re-rate, so the
// per-unit re-rate section is omitted and only the address fields that exist
// on the Business `address_change` shape are shown:
//   - "Change" header + explanatory copy.
//   - Address Type Required selector.
//   - "Change address on customer screen?" Yes/No (reuses the sibling field
//     `change_mailing_address_on_customer`).
//   - Address search + address 2, city, province, postal code, country.
//
// Kept separate from the shared V1 AddressChangeInformation step so V1 (and
// other reuse of that step) is unaffected.

import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import produce from "immer";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../../components/formsComponents";
import { CustomCheckbox } from "../../../../../../../../components/formsComponents/CustomCheckbox";
import { AddressSearchAutoComplete } from "../../../../../../../../components/AddressAutoComplete/AddressSearchAutoComplete";
import { ILocationResponsePayload } from "../../../../../../../../components/LocationAutoComplete/LocationAutoComplete";
import { provincesAndStates } from "../../../../../../../../constants/constants";
import { IBusinessPolicyChangeStepsProps } from "../../PolicyChange.types";

// This panel renders inside a RightPanel with zIndex 1400 (see
// ChangeSectionDrawer). MUI's Select opens its menu in a Popover portalled at
// the document root with the default modal z-index (1300), so without this
// override every dropdown menu in this panel renders behind the drawer and
// its options appear "missing" (the menu is open, just hidden underneath).
const SELECT_MENU_PROPS = { sx: { zIndex: 1500 } };

// Address type options rendered as a pill toggle group, matching the
// Automobile V2 address panel's selector styling.
const ADDRESS_TYPE_OPTIONS = [
  "Mailing Address",
  "Garaging Location",
  "Mailing Address & Garaging Location",
] as const;

export const AddressChangeInformationV2: React.FC<
  IBusinessPolicyChangeStepsProps
> = ({ values, setValues, handleChange }) => {
  const addressChange = values.address_change;

  const handleAddressTypeSelect = (
    _e: React.MouseEvent<HTMLElement>,
    value: (typeof ADDRESS_TYPE_OPTIONS)[number] | null
  ) => {
    if (!value) {
      return;
    }
    setValues(
      produce(values, (draft) => {
        draft.address_change.address_type = value;
      })
    );
  };

  const handleAddress = (data: ILocationResponsePayload) => {
    setValues({
      ...values,
      address_change: {
        ...addressChange,
        address: data.address,
        city: data.city,
        country: data.country,
        state: data.state,
        postal_code: data.postalCode,
      },
    });
  };

  const handleChangeOnCustomerScreen = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValues(
      produce(values, (draft) => {
        draft.change_mailing_address_on_customer = e.target.value;
      })
    );
  };

  return (
    <>
      {/* "Change" header, matching the Automobile V2 mockup. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CustomCheckbox checked={!!addressChange.address} disabled sx={{ p: 0 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Change
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Update the insured's address on this policy. Choose whether the change
        should also be reflected on the customer screen.
      </Typography>

      {/* Address Type Required — pill toggle selector (matches Automobile V2). */}
      <Box sx={{ mb: 2 }}>
        <CustomFormLabel sx={{ mt: 0 }}>Address Type Required</CustomFormLabel>
        <ToggleButtonGroup
          value={addressChange.address_type}
          exclusive
          size="small"
          onChange={handleAddressTypeSelect}
          sx={{
            borderRadius: "50px",
            border: "1px solid",
            borderColor: "divider",
            gap: 0.25,
            p: 0.4,
            flexWrap: "wrap",
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              "&:not(:first-of-type)": { borderRadius: "50px" },
              "&:first-of-type": { borderRadius: "50px" },
            },
          }}
        >
          {ADDRESS_TYPE_OPTIONS.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{
                px: 2,
                py: 0.6,
                fontSize: "0.85rem",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
              }}
            >
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Change address on customer screen? */}
      <Box sx={{ mb: 2 }}>
        <CustomFormLabel sx={{ mt: 0 }}>
          Change address on customer screen? *
        </CustomFormLabel>
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="change_mailing_address_on_customer"
            value={values.change_mailing_address_on_customer || ""}
            onChange={handleChangeOnCustomerScreen}
          >
            <FormControlLabel value="Y" control={<Radio />} label="Yes" />
            <FormControlLabel value="N" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Address fields laid out two-per-row, matching Automobile V2's
          narrow-drawer address panel (label above each field). */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <AddressSearchAutoComplete
            address={addressChange.address}
            onAddressChange={handleAddress}
            // This panel renders in a RightPanel with zIndex 1400 (see
            // ChangeSectionDrawer); without this the "Search Address" dialog
            // opens at MUI's default modal z-index (1300) and ends up stacked
            // behind the drawer instead of on top of it.
            dialogZIndex={1500}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomFormLabel>Address 2</CustomFormLabel>
          <CustomTextField
            name="address_change.address_two"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.address_two || ""}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomFormLabel>City</CustomFormLabel>
          <CustomTextField
            name="address_change.city"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.city || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomFormLabel>State/Province</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="address_change.state"
            value={addressChange.state}
            onChange={handleChange}
            placeholder="Select"
            options={provincesAndStates}
            MenuProps={SELECT_MENU_PROPS}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomFormLabel>Postal Code</CustomFormLabel>
          <CustomTextField
            name="address_change.postal_code"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.postal_code || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomFormLabel>Country</CustomFormLabel>
          <CustomTextField
            name="address_change.country"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.country || ""}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </>
  );
};
